import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TheCrypt} from "../TheCrypt";
import {TheCryptSymbol, ISymbolData} from "../symbols/TheCryptSymbol";
import {TheCryptModel} from "../server/TheCryptModel";
import {TheCryptAvalancheReelModel} from "./TheCryptAvalancheReelModel";
import {TheCryptGameConfig} from "../TheCryptGameConfig";
import {TimelineLite, TweenLite} from "gsap";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";

export class TheCryptReelArea extends PIXI.Container implements StageLayer {
    public readonly DROP_IN_REEL_DELAY: number = 0.1;
    private readonly FAST_DROP_IN_REEL_DELAY: number = 0.05;
    private readonly FULL_UP_REEL_DELAY: number = 0.05;
    private readonly DROP_OUT_REEL_DELAY: number = 0.02;
    private readonly FILL_UP_REEL_DELAY: number = 0.05;
    private readonly COLLAPSE_REEL_DELAY: number = 0.05;
    private readonly DROP_IN_NEAR_WIN_DELAY: number = 1;
    private readonly FAST_DROP_IN_NEAR_WIN_DELAY: number = 0.3;
    private _allReels: TheCryptAvalancheReelModel[];
    private _needFillUpReels: boolean[];
    private _dimmer: PIXI.Sprite;

    get isFastSpin(): boolean {
        if (TheCrypt.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            return true;
        }

        if (TheCrypt.model.data.wasFeatureBuy && (TheCrypt.model.isNormal && !TheCrypt.model.isLuckyDraw)) {
            return true;
        }

        return false;
    }

    get hasAnticipation(): boolean {
        if (TheCrypt.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            return false;
        }

        return true;
    }

    private creatDimmer(): void {
        this._dimmer = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._dimmer.name = "dimmer";
        this._dimmer.width = this.getReelAreaWidth();
        this._dimmer.height = this.getReelAreaHeight();
        this._dimmer.position.set(-TheCryptGameConfig.symbolWidth * 0.5, 0);
        this._dimmer.tint = 0x000000;
        this._dimmer.alpha = 0.7;
        this._dimmer.visible = false;
    }

    constructor() {
        super();
        this.init();
    }

    private init(): void {
        this.name = "ReelAreaView";
        this._allReels = [];

        const data: TheCryptModel = TheCrypt.model,
            reelsData: ISymbolData[][] = data.reelsData;
        let tempReel: ISymbolData[];

        for (let i: number = 0; i < reelsData.length; i++) {
            tempReel = [];
            const reelModel: TheCryptAvalancheReelModel = new TheCryptAvalancheReelModel(i);
            this._allReels.push(reelModel);
            this.addChild(reelModel.reelView);
            reelModel.init(reelsData[i]);
        }

        for (let j: number = 0; j < this._allReels.length; j++) {
            TheCrypt.highSymbolRealArea.addChild(this._allReels[j].reelView.fullWildSymbolView);
            TheCrypt.highSymbolRealArea.addChild(this._allReels[j].reelView.scatterSymbolView);
            TheCrypt.symbolAnimation.addChild(this._allReels[j].reelView.initialWinSymbolView);
            TheCrypt.symbolAnimation.addChild(this._allReels[j].reelView.fullWildSymbolWinView);
            TheCrypt.symbolAnimation.addChild(this._allReels[j].reelView.scatterWinView);
        }

        const mainReelsMask: PIXI.Graphics = new PIXI.Graphics();
        mainReelsMask.name = "reelAreaMask";
        mainReelsMask.beginFill(0xFFFF0B, 0.5);
        mainReelsMask.drawRect(-TheCryptGameConfig.symbolWidth, 0, this.getReelAreaWidth() + TheCryptGameConfig.symbolWidth, this.getReelAreaHeight());

        // todo enable reel area masking
        this.addChild(mainReelsMask);
        this.mask = mainReelsMask;
        this.position = TheCryptGameConfig.reelAreaPos;
        this.creatDimmer();
    }

    public reInitReels(){
        const reelsData: ISymbolData[][] = TheCrypt.model.reelsData;
        for (let i: number = 0; i < reelsData.length; i++) {
            const reel = this._allReels[i];
            reel.reInitReels(reelsData[i]);
        }
    }

    private getReelAreaWidth(): number {
        return (TheCryptGameConfig.symbolWidth * TheCryptGameConfig.reelsNum) + 20;
    }

    private getReelAreaHeight(): number {
        return TheCryptGameConfig.symbolHeight * 4;
    }

    public get needFillUpReels(): boolean[] {
        return this._needFillUpReels;
    }

    public addDimmer(): TimelineLite {
        return new TimelineLite().add(() => {
            TheCrypt.highSymbolRealArea.addChild(this._dimmer);
            this._dimmer.alpha = 0;
            this._dimmer.visible = true;
        }).add(TweenLite.to(this._dimmer, 0.2, {alpha: 0.7}));
    }

    public removeDimmer(): TimelineLite {
        return new TimelineLite().to(this._dimmer, 0.2, {alpha: 0}).add(() => {
            this._dimmer.visible = false;
        });
    }

    public dropIn(jurisdictionDelay: number): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [],
            nearWinReels: boolean[] = TheCrypt.model.nearWinReels,
            hasAnticipation: boolean = this.hasAnticipation,
            nearWinDelay: number = this.hasAnticipation ? this.DROP_IN_NEAR_WIN_DELAY : this.FAST_DROP_IN_NEAR_WIN_DELAY,
            dropInDelay: number = this.isFastSpin ? this.FAST_DROP_IN_REEL_DELAY : this.DROP_IN_REEL_DELAY,
            padding: number = MathHelper.roundToDecimals(jurisdictionDelay / 5, 1);
        this._needFillUpReels = [true,true,true,true,true,true];
        TheCryptSoundManager.newAvalanche = true;
        let delay: number = 0;
        this._allReels.forEach((reel: TheCryptAvalancheReelModel, i: number) => {
            delay += dropInDelay + (nearWinReels[i] ? nearWinDelay : 0);
            animations.push(reel.reelView.dropIn(delay + padding * i, hasAnticipation));
        });
        return animations;
    }

    public dropOut(): TimelineLite {
        const animations: TimelineLite = new TimelineLite();
        this._allReels.forEach((reel: TheCryptAvalancheReelModel, i: number) => {
            animations.add(reel.reelView.dropOut(Math.abs(2.5 - i) * this.DROP_OUT_REEL_DELAY), 0);
        });
        return animations;
    }

    public fillUp(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [],
            nearWinReels: boolean[] = TheCrypt.model.nearWinReels,
            hasAnticipation: boolean = this.hasAnticipation;
        let delay: number = 0;
        TheCryptSoundManager.newAvalanche = true;
        this._needFillUpReels = [true,true,true,true,true,true];
        this._allReels.forEach((reel: TheCryptAvalancheReelModel, i: number) => {
            if (reel.reelView.needFillUp) {
                delay += (i * this.FULL_UP_REEL_DELAY + ((nearWinReels[i] && i > 0) ? this.DROP_IN_NEAR_WIN_DELAY : 0));
                animations.push(reel.reelView.fillUp(delay, hasAnticipation));
            }else{
                this._needFillUpReels[i] = false;
            }
        });
        return animations;
    }

    public collapse(): gsap.core.Timeline[] {
        const animations: gsap.core.Timeline[] = [];
        let delay: number = 0;
        this._allReels.forEach((reel: TheCryptAvalancheReelModel) => {
            reel.reelView.calculateCollapseData();
            if (reel.reelView.needCollapse) {
                animations.push(reel.reelView.collapse(delay));
                delay += this.COLLAPSE_REEL_DELAY;
            }
        });
        return animations;
    }

    public getSymbol(pos: number[]): TheCryptSymbol {
        return this._allReels[pos[0]].reelView.getSymbol(pos[1]);
    }

    get allReels(): TheCryptAvalancheReelModel[] {
        return this._allReels;
    }
}